import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  ContentCopy,
  Visibility,
  VisibilityOff,
  QrCode2,
  Key,
  CheckCircle,
} from '@mui/icons-material';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { verify2FA } from '@/store/slices/authSlice';
import toast from 'react-hot-toast';

interface TwoFactorDialogProps {
  open: boolean;
  onClose: () => void;
  tempToken: string;
  isSetup?: boolean;
  qrCode?: string;
  secret?: string;
}

const TwoFactorDialog: React.FC<TwoFactorDialogProps> = ({
  open,
  onClose,
  tempToken,
  isSetup = false,
  qrCode,
  secret,
}) => {
  const dispatch = useAppDispatch();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [backupCodesSaved, setBackupCodesSaved] = useState(false);

  const steps = isSetup
    ? ['Escanear QR Code', 'Verificar Código', 'Salvar Códigos de Backup']
    : [];

  useEffect(() => {
    if (open) {
      setCode('');
      setError('');
      setActiveStep(0);
      setBackupCodes([]);
      setBackupCodesSaved(false);
    }
  }, [open]);

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Aceitar códigos TOTP (6 dígitos) ou códigos de backup (formato XXXX-XXXX-XXXX)
    const value = e.target.value.toUpperCase().replace(/[^0-9A-F-]/g, '');
    if (value.length <= 14) { // 12 chars + 2 hyphens
      setCode(value);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência');
  };

  const copyAllBackupCodes = () => {
    const text = backupCodes.join('\n');
    navigator.clipboard.writeText(text);
    toast.success('Todos os códigos copiados');
  };

  const handleSubmit = async () => {
    const cleanCode = code.replace(/-/g, '');
    if (cleanCode.length !== 6 && cleanCode.length !== 12) {
      setError('Digite um código TOTP de 6 dígitos ou um código de backup');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await dispatch(
        verify2FA({ code, twoFactorToken: tempToken })
      ).unwrap();

      if (result) {
        // Se é setup e tem códigos de backup, mostrar
        if (isSetup && result.backupCodes && result.backupCodes.length > 0) {
          setBackupCodes(result.backupCodes);
          setActiveStep(2);
          toast.success('2FA configurado com sucesso!');
        } else {
          toast.success('Login realizado com sucesso!');
          onClose();
        }
      }
    } catch (err: any) {
      setError(err.message || err || 'Código inválido');
      toast.error('Código de verificação inválido');
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    if (!backupCodesSaved) {
      toast.error('Confirme que salvou os códigos de backup');
      return;
    }
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      if (activeStep === 0 && isSetup) {
        setActiveStep(1);
      } else if (code.length >= 6) {
        handleSubmit();
      }
    }
  };

  // Render setup view (with QR code)
  const renderSetupView = () => (
    <Box>
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Escaneie o QR Code abaixo com seu aplicativo autenticador
            (Google Authenticator, Authy, Microsoft Authenticator, etc.)
          </Typography>

          {qrCode && (
            <Paper
              elevation={2}
              sx={{
                p: 2,
                mb: 2,
                display: 'inline-block',
                backgroundColor: '#fff',
              }}
            >
              <img src={qrCode} alt="QR Code 2FA" style={{ width: 200, height: 200 }} />
            </Paper>
          )}

          <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
            Não consegue escanear? Use o código manual:
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
            <Paper
              sx={{
                px: 2,
                py: 1,
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                letterSpacing: 1,
                backgroundColor: '#f5f5f5',
              }}
            >
              {showSecret ? secret : '••••••••••••••••••••••••••••••••'}
            </Paper>
            <Tooltip title={showSecret ? 'Ocultar' : 'Mostrar'}>
              <IconButton size="small" onClick={() => setShowSecret(!showSecret)}>
                {showSecret ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Copiar">
              <IconButton size="small" onClick={() => copyToClipboard(secret || '')}>
                <ContentCopy />
              </IconButton>
            </Tooltip>
          </Box>

          <Button
            variant="contained"
            onClick={() => setActiveStep(1)}
            startIcon={<Key />}
          >
            Continuar
          </Button>
        </Box>
      )}

      {activeStep === 1 && (
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Digite o código de 6 dígitos mostrado no seu aplicativo autenticador
            para confirmar a configuração
          </Typography>

          <TextField
            autoFocus
            fullWidth
            value={code}
            onChange={handleCodeChange}
            onKeyPress={handleKeyPress}
            placeholder="000000"
            inputProps={{
              style: {
                fontSize: '24px',
                letterSpacing: '8px',
                textAlign: 'center',
              },
              maxLength: 6,
            }}
            sx={{ my: 3 }}
            disabled={loading}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setActiveStep(0)} disabled={loading}>
              Voltar
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={code.length !== 6 || loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Verificando...' : 'Confirmar'}
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 2 && (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              Guarde estes códigos em um local seguro!
            </Typography>
            <Typography variant="caption">
              Você pode usar estes códigos para acessar sua conta caso perca acesso ao seu
              aplicativo autenticador. Cada código só pode ser usado uma vez.
            </Typography>
          </Alert>

          <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Códigos de Backup
              </Typography>
              <Button size="small" startIcon={<ContentCopy />} onClick={copyAllBackupCodes}>
                Copiar Todos
              </Button>
            </Box>
            <Divider sx={{ mb: 1 }} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 1,
                fontFamily: 'monospace',
              }}
            >
              {backupCodes.map((code, index) => (
                <Chip
                  key={index}
                  label={code}
                  variant="outlined"
                  size="small"
                  sx={{ fontFamily: 'monospace', justifyContent: 'flex-start' }}
                />
              ))}
            </Box>
          </Paper>

          <Box sx={{ mb: 2 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={backupCodesSaved}
                onChange={(e) => setBackupCodesSaved(e.target.checked)}
              />
              <Typography variant="body2">
                Confirmo que salvei os códigos de backup em um local seguro
              </Typography>
            </label>
          </Box>

          <Button
            fullWidth
            variant="contained"
            color="success"
            onClick={handleFinish}
            disabled={!backupCodesSaved}
            startIcon={<CheckCircle />}
          >
            Concluir Configuração
          </Button>
        </Box>
      )}
    </Box>
  );

  // Render verification view (when 2FA is already set up)
  const renderVerificationView = () => (
    <Box sx={{ pt: 2 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Digite o código de 6 dígitos do seu aplicativo autenticador ou um código de backup
      </Typography>

      <TextField
        autoFocus
        fullWidth
        value={code}
        onChange={handleCodeChange}
        onKeyPress={handleKeyPress}
        placeholder="000000"
        inputProps={{
          style: {
            fontSize: '24px',
            letterSpacing: '8px',
            textAlign: 'center',
          },
          maxLength: 14,
        }}
        sx={{ my: 3 }}
        disabled={loading}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary">
        Abra seu aplicativo autenticador (Google Authenticator, Authy, etc.)
        e digite o código de 6 dígitos mostrado. Você também pode usar um código de backup.
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={(!loading && activeStep !== 2) ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading || activeStep === 2}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <QrCode2 color="primary" />
        {isSetup ? 'Configurar Autenticação em Duas Etapas' : 'Verificação em Duas Etapas'}
      </DialogTitle>
      <DialogContent>
        {isSetup ? renderSetupView() : renderVerificationView()}
      </DialogContent>
      {!isSetup && (
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={code.length < 6 || loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Verificando...' : 'Verificar'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default TwoFactorDialog;
